import moment from 'moment-timezone';

export const today = moment();
/**
 * Create a moment from a ISO 8601 date string.
 */
export function isoMoment(isoDate) {
    return moment(isoDate, moment.ISO_8601);
}

/**
 * Format an iso 8601 date string into the specified date format.
 *
 * @param {*} isoDate an ios 8601 date string.
 * @param {*} format @see moment.format
 *
 * @see moment#format
 */
export function formatDate(isoDate, format) {
    const date = isoMoment(isoDate);
    const formattedDate = date.format(format);
    return formattedDate;
}

/**
 * Format an iso 8601 date string into MMMM D, YYYY
 *
 * e.g. May 4, 2020
 */
export function formatDateMMMMDYYYY(isoDate) {
    return formatDate(isoDate, 'MMMM D, YYYY');
}

/**
 * Format an iso 8601 date string into MMM D, YYYY
 *
 * e.g. Jan 1, 2020
 */
export function formatDateMMMDYYYY(isoDate) {
    return formatDate(isoDate, 'MMM D, YYYY');
}

/**
 * Get a display string that describes the number of days between now and the provided due date.
 * If the date is in the future, the description is 'x days left'.
 * If the date is in the past, the description is 'x days late'.
 * If the date is today, the description is due today.
 *
 * @param dueDate The provided isoDate
 * @param onlyDayCount Optional. If true, only day count is returned e.g 5 instead of '5 days left'
 * @returns a string representing the number of days between now and the provided date.
 *          e.g. '5 days left'
 */
export function daysLeftFromToday(dueDate, onlyDayCount) {
    const normalizedDueDate = isoMoment(dueDate).startOf('day');
    const normalizedNowDate = moment().startOf('day');
    const dayDiff = normalizedDueDate.diff(normalizedNowDate, 'days');
    if (dayDiff === 0) {
        return onlyDayCount ? 0 : 'Due today';
    }

    if (onlyDayCount) {
        return dayDiff;
    }

    const absDayDiff = Math.abs(dayDiff);

    const dayOrDays = absDayDiff > 1 ? 'days' : 'day';

    let leftOrLate = '';
    if (dayDiff > 0) {
        leftOrLate = 'left';
    } else {
        leftOrLate = 'late';
    }

    return `${absDayDiff} ${dayOrDays} ${leftOrLate}`;
}

/**
 * Compute number of minutes between two unix time stamps
 * @param timeA  string representing number of milliseconds of time A since the Unix Epoch (Jan 1 1970 12AM UTC)
 * @param timeB  string representing number of milliseconds of time B since the Unix Epoch (Jan 1 1970 12AM UTC)
 */
export function computeMinutes(timeA, timeB) {
    const momentA = moment(parseInt(timeA));
    const momentB = moment(parseInt(timeB));

    const diffInMinutesWithPrecision = momentA.diff(momentB, 'minutes', true);
    let roundedDiffInMinutes = Math.round(diffInMinutesWithPrecision);

    if (roundedDiffInMinutes < diffInMinutesWithPrecision) {
        return roundedDiffInMinutes + 1; // adding 1 to offset rounding down, i.e treat 0.49 like 1, and not 0
    } else {
        return roundedDiffInMinutes;
    }
}

/**
 * custom YearMonth class
 */

export class YearMonth {
    constructor(year, month) {
        this.year = year;
        this.month = month;
    }

    isBefore(yearMonth) {
        if (this.year < yearMonth.year) {
            return true;
        } else if (this.year === yearMonth.year && this.month < yearMonth.month) {
            return true;
        } else {
            return false;
        }
    }

    isAfter(yearMonth) {
        if (this.year > yearMonth.year) {
            return true;
        } else if (this.year === yearMonth.year && this.month > yearMonth.month) {
            return true;
        } else {
            return false;
        }
    }

    isSame(yearMonth) {
        if (this.year === yearMonth.year && this.month === yearMonth.month) {
            return true;
        } else {
            return false;
        }
    }
}

export function getTimeLeftToOpenVisit(visitOpenDate) {
    const nowDate = isoMoment(moment());

    if (!nowDate.isBefore(visitOpenDate)) {
        return '';
    }

    const years = visitOpenDate.diff(nowDate, 'years');
    const months = visitOpenDate.diff(nowDate, 'months') % 12;
    const days = visitOpenDate.diff(nowDate, 'days') % 30; // using 30 days in month

    const yearsLeftStr = years < 1 ? '' : years === 1 ? '1YR ' : `${years}YRS `;
    const monthsLeftStr = months < 1 ? '' : months === 1 ? '1MTH' : `${months}MTHS`;
    const daysLeftStr = years < 1 && months < 1 && days >= 1 ? (days === 1 ? '1DAY' : `${days}DAYS`) : '';

    return yearsLeftStr + monthsLeftStr + daysLeftStr;
}

export const yearsNumList = (start, end) => {
    return Array(end - start + 1)
        .fill(start)
        .map((year, index) => year + index);
};

export const getMonthMarker = (startDate, current = today) => {
    if(!startDate || !startDate.isValid()) return 0;
    return getTotalMonthsBetweenDates(startDate, current) % 12;
}

export const getTotalMonthsBetweenDates = (startDate, endDate) => {
    if(!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) return 0;
    return Math.ceil(endDate.diff(startDate, 'months', true));
}

export const yearRangesList = (startDate, endDate) => {
    const yearRanges = [];
    let from = moment(startDate);   // copy start date
    let to = moment(from).add(1, 'year');  // add 1 year for first
    let reachedEnd = false;

    while (!reachedEnd) {
        yearRanges.push({
            yearStartDate: from,
            yearEndDate: to,
            monthsInYear: Math.ceil(to.diff(from,'months',true))
        });

        if(to.isSameOrAfter(endDate)) {
            reachedEnd = true;
        }

        const diffNow = endDate.diff(to, 'months',true);

        if (diffNow >= 12) {
            from = moment(from).add(1, 'year');
            to = moment(from).add(1, 'year');
        } else if (diffNow > 0) {
            from = moment(from).add(1, 'year');
            to = moment(from).add(Math.ceil(diffNow), 'months');
        } else {
            reachedEnd = true;
        }
    }

    return yearRanges;
}

export const visitYearRelativeToDiagnosisDate = (visitOpenDate, dateOfDiagnosis = null) => {

    if(!dateOfDiagnosis || !visitOpenDate) {
        return; 
    }

    // Rule: If the current visit time is equal to (or greater than) the DoD to the second,
    // the visit date falls on the next year.
    if(visitOpenDate.format('HH:mm:ss') === dateOfDiagnosis.format('HH:mm:ss')) {
        visitOpenDate = visitOpenDate.add(1, 'seconds');
    }

    const diff = Math.ceil(visitOpenDate.diff(dateOfDiagnosis, 'years', true));
    return diff === 0 ? 1 : diff;
}

export const getClinicHoursStatus = (currentTime) => {
    // Clinic is open 8 am - 4 pm, Monday to Friday
    const clinicOpenTime = '08:00:00.000';
    const clinicCloseTime = '16:00:00.000';
    const clinicTimeZone = 'America/Toronto';
    const dateTimeStringFormat = 'YYYY-MM-DD HH:mm:ss.SSS';

    let currentTorontoTime = moment(currentTime).tz(clinicTimeZone, false);

    let currentDateString = moment(currentTorontoTime).format('YYYY-MM-DD');
    const openTime = moment.tz(currentDateString + ' ' + clinicOpenTime, dateTimeStringFormat, clinicTimeZone);
    const closeTime = moment.tz(currentDateString + ' ' + clinicCloseTime, dateTimeStringFormat, clinicTimeZone);

    let clinicIsClosed;
    let timeOfNextUpdate;

    if (moment(currentTorontoTime).isoWeekday() === 6 || moment(currentTorontoTime).isoWeekday() === 7) {
        clinicIsClosed = true;
        timeOfNextUpdate = moment(openTime).isoWeekday(8); // update on the next Monday
    } else if (moment(currentTorontoTime).isBefore(openTime, 'millisecond')) {
        clinicIsClosed = true;
        timeOfNextUpdate = openTime;
    } else if (moment(currentTorontoTime).isSameOrAfter(closeTime, 'millisecond')) {
        clinicIsClosed = true;
        timeOfNextUpdate = moment(currentTorontoTime).isoWeekday() === 5
            ? moment(openTime).isoWeekday(8) : moment(openTime).add(1, 'day'); // update on the next weekday
    } else {
        clinicIsClosed = false;
        timeOfNextUpdate = closeTime;
    }

    let timeUntilUpdate = timeOfNextUpdate.diff(currentTorontoTime, 'milliseconds');

    return { clinicIsClosed, timeUntilUpdate };
}